import React, { useEffect, useState, useRef } from "react";
import { Search as SearchIcon, ChevronDown, MoreVertical, ArrowLeft, GitBranch, MapPin, Mail, Phone, User, Activity } from "lucide-react";
import { toast } from "sonner";
import ExportPrintActions, { ColumnConfig } from "../../utils/exportUtils";
import { FleetrifyCustomTable, Column } from "../CustomTable/customTable";
import { useGetManagersQuery } from "../../redux/services/branchApi";
import { FleetrifyModal } from "../Modal/modal";
import { useSingleUserController } from "../../controllers/useSingleUserController";
import { usePermission } from "../../hooks/usePermission";
import FleetrifyTooltip from "../Tooltip/tooltip";

export interface BranchData {
  id: string;
  branchName: string;
  location: string;
  address: string;
  branchEmail: string;
  managerName: string;
  managerId?: string;
  contactNumber: string;
  status?: string;
  activeOrders?: string;
  time?: string;
}

type Props = {
  tableData: BranchData[];
  isLoading?: boolean;
  pageSize?: number;
  total?: number;
  currentPage?: number;
  lastPage?: number;
  setCurrentPage?: (param: number) => void;
  onEdit: (id: string, data: BranchData) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onDeactivate: (id: string) => Promise<void>;
  onReactivate: (id: string) => Promise<void>;
  isAdmin: boolean;
  can: (permission: string) => boolean;
};

const BranchTable = ({
  tableData,
  isLoading = false,
  pageSize,
  total,
  currentPage = 1,
  lastPage = 1,
  setCurrentPage,
  onEdit,
  onDelete,
  onDeactivate,
  onReactivate,
  isAdmin,
  can,
}: Props) => {
  const [tableSearchTerm, setTableSearchTerm] = useState("");
  const [selectedRow, setSelectedRow] = useState<BranchData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showManagerDropdown, setShowManagerDropdown] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const actionMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState<BranchData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { hasPermission } = usePermission();

  const { userDetails } = useSingleUserController();
  const getRoleName = (roleData: any): string => {
    if (typeof roleData === "object" && roleData?.name) return roleData.name;
    return typeof roleData === "string" ? roleData : "Role";
  };
  const role = getRoleName(userDetails?.role);

  const { data: managersData, isLoading: isManagersLoading, error: isManagersError } =
    useGetManagersQuery();

  const managers =
    managersData?.data?.staff_members?.map((m: any) => ({
      id: String(m.id),
      name: m.user?.name ?? "Unnamed",
    })) ?? [];

  const handleRowClick = (row: BranchData) => {
    setSelectedRow(row);
    setIsEditing(false);
  };

  const filteredTableData = tableData.filter((row) =>
    Object.values(row).some((value) =>
      String(value).toLowerCase().includes(tableSearchTerm.toLowerCase())
    )
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowManagerDropdown(false);
      }
      const clickedOutsideAllMenus = Object.values(actionMenuRefs.current).every(
        (ref) => !ref || !ref.contains(event.target as Node)
      );
      if (clickedOutsideAllMenus) setOpenActionMenuId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleActionClick = async (
    action: "edit branch" | "deactivate branch" | "activate branch" | "delete branch",
    row: BranchData
  ) => {
    if (!hasPermission(action)) {
      toast.error("You do not have permission to perform this action");
      setOpenActionMenuId(null);
      return;
    }
    try {
      switch (action) {
        case "edit branch":
          setSelectedRow(row);
          setIsEditing(true);
          break;
        case "deactivate branch":
          await onDeactivate(row.id);
          break;
        case "activate branch":
          await onReactivate(row.id);
          break;
        case "delete branch":
          setBranchToDelete(row);
          setShowDeleteModal(true);
          break;
      }
    } catch {
      
    }
    setOpenActionMenuId(null);
  };

  const handleConfirmDelete = async () => {
    if (!branchToDelete) return;
    if (!hasPermission("delete branch")) {
      toast.error("You do not have permission to delete this branch");
      return;
    }
    try {
      setIsDeleting(true);
      await onDelete(branchToDelete.id);
    } catch {
      
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setBranchToDelete(null);
    }
  };

  const handleUpdateClick = async () => {
    if (!selectedRow) return;
    if (!hasPermission("edit branch")) {
      toast.error("You do not have permission to update this branch");
      return;
    }
    try {
      await onEdit(selectedRow.id, selectedRow);
      setSelectedRow(null);
      setIsEditing(false);
    } catch {
      
    }
  };

  const columns: Column[] = [
    { label: "Branch Name", key: "branchName", sortable: true },
    { label: "Location", key: "location", sortable: true },
    { label: "Manager", key: "managerName", sortable: true },
    // { label: "Active Orders", key: "activeOrders", sortable: true },
    { label: "Time", key: "time", sortable: true },
    {
      label: "Status",
      key: "status",
      sortable: true,
      render: (_: any, row: BranchData) => (
        <span className={`px-3 py-1 rounded-full border text-sm font-medium ${row.status === "Active"
            ? "border-green-500 text-green-700 bg-green-50"
            : "border-red-500 text-red-700 bg-red-50"
          }`}>
          {row.status}
        </span>
      ),
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      render: (_: any, row: BranchData) => (
        <div className="relative" ref={(el) => { actionMenuRefs.current[row.id] = el; }}>
          <button
            onClick={(e) => { e.stopPropagation(); setOpenActionMenuId(openActionMenuId === row.id ? null : row.id); }}
            className="p-1 hover:bg-gray-100 rounded transition"
          >
            <MoreVertical size={18} className="text-gray-600" />
          </button>
          {openActionMenuId === row.id && (
            <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
              <ul className="py-1 text-sm">
                <li onClick={(e) => { e.stopPropagation(); handleActionClick("edit branch", row); }} className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-gray-700 transition">Update</li>
                {row.status === "Active" ? (
                  <li onClick={(e) => { e.stopPropagation(); handleActionClick("deactivate branch", row); }} className="px-4 py-2 hover:bg-orange-50 cursor-pointer text-orange-600 transition">
                    <FleetrifyTooltip
                      position="left"
                      className="block"
                      contentClassName="whitespace-normal w-48 text-center"
                      content="Marks this branch as inactive and removes it from selection for new assignments. It won't delete the branch or any of its existing data."
                    >
                      Deactivate
                    </FleetrifyTooltip>
                  </li>
                ) : (
                  <li onClick={(e) => { e.stopPropagation(); handleActionClick("activate branch", row); }} className="px-4 py-2 hover:bg-green-50 cursor-pointer text-green-600 transition">Reactivate</li>
                )}
                <li onClick={(e) => { e.stopPropagation(); handleActionClick("delete branch", row); }} className="px-4 py-2 hover:bg-red-50 cursor-pointer text-red-600 transition">Delete</li>
              </ul>
            </div>
          )}
        </div>
      ),
    },
  ];

  const exportColumns: ColumnConfig[] = columns
    .filter((col) => col.key !== "actions")
    .map((col) => ({ key: String(col.key), label: col.label }));

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-600">
        <div className="w-10 h-10 border-4 border-[#143887] border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm">Loading branches...</p>
      </div>
    );
  }

  // if (!tableData.length) {
  //   return <div className="p-6 text-center text-gray-500">No branches found.</div>;
  // }

  if (!tableData.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 bg-[#E7E9EE] rounded-full grid place-items-center mb-4">
          <svg className="w-8 h-8 text-[#A1AFCF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <h3 className="text-base font-semibold text-[#00032A] mb-1">No branches found</h3>
        <p className="text-sm text-gray-500 text-center max-w-xs">No branches have been created yet.</p>
      </div>
    );
  }

  const inputClass = "mt-1 block w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#00032A] bg-white focus:outline-none focus:ring-2 focus:ring-[#143887]/30 focus:border-[#143887] transition-colors placeholder:text-gray-300";

  return (
    <div className="space-y-4">

      {/* ── Table view ── */}
      {!selectedRow && (
        <>
          <div className="no-print flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center w-full md:w-[50%] lg:w-[30%] border border-gray-200 rounded-full px-3 bg-white">
              <SearchIcon className="text-gray-400 mr-2 flex-shrink-0" size={16} />
              <input
                type="text"
                placeholder="Search branches..."
                value={tableSearchTerm}
                onChange={(e) => setTableSearchTerm(e.target.value)}
                className="w-full py-2 outline-none text-sm bg-transparent"
              />
            </div>
            <div className="flex justify-end w-full mt-3 md:mt-0 md:w-auto">
              <ExportPrintActions rows={filteredTableData} columns={exportColumns} fileName="branches" />
            </div>
          </div>

          <div id="print-section">
            <FleetrifyCustomTable
              onRowClick={handleRowClick}
              data={filteredTableData}
              columns={columns}
              tooltipLeft
              pageSize={pageSize}
              currentPage={currentPage}
              lastPage={lastPage}
              total={total}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </>
      )}

      {/* ── Detail / Edit view ── */}
      {selectedRow && (
        <div>
          {/* Back button */}
          <button
            type="button"
            onClick={() => { setSelectedRow(null); setIsEditing(false); }}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#143887] font-medium transition-colors group mb-5 no-print"
          >
            <span className="flex items-center justify-center w-7 h-7 rounded-full border border-gray-200 group-hover:border-[#143887] group-hover:bg-[#e4ebf8] transition-all">
              <ArrowLeft size={14} />
            </span>
            Back to Branches
          </button>

          {/* Header card */}
          <div className="bg-gradient-to-r from-[#143887] to-[#1e4fa3] rounded-2xl p-5 mb-5 text-white">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-white/15">
                  <GitBranch size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-blue-200 text-xs font-medium uppercase tracking-widest mb-0.5">Branch Details</p>
                  <h2 className="text-xl font-bold">{selectedRow.branchName}</h2>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${selectedRow.status === "Active"
                  ? "bg-emerald-400/20 text-emerald-300 border border-emerald-400/30"
                  : "bg-red-400/20 text-red-300 border border-red-400/30"
                }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${selectedRow.status === "Active" ? "bg-emerald-400" : "bg-red-400"}`} />
                {selectedRow.status}
              </span>
            </div>

            {/* Quick info pills */}
            <div className="flex flex-wrap gap-2 mt-4">
              {selectedRow.location && (
                <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-3 py-1.5 text-xs text-blue-100">
                  <MapPin size={11} />
                  {selectedRow.location}
                </div>
              )}
              {selectedRow.managerName && (
                <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-3 py-1.5 text-xs text-blue-100">
                  <User size={11} />
                  {selectedRow.managerName}
                </div>
              )}
              {selectedRow.activeOrders && (
                <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-3 py-1.5 text-xs text-blue-100">
                  <Activity size={11} />
                  {selectedRow.activeOrders} active orders
                </div>
              )}
            </div>
          </div>

          {/* Edit form card */}
          <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
            <p className="text-xs text-gray-400 mb-5 flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-[#143887] inline-block" />
              Click any field to edit
            </p>

            <form onSubmit={(e) => { e.preventDefault(); handleUpdateClick(); }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

                {/* Branch Name */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Branch Name</label>
                  <input type="text" value={selectedRow.branchName} placeholder="Enter branch name"
                    onChange={(e) => { setSelectedRow({ ...selectedRow, branchName: e.target.value }); setIsEditing(true); }}
                    className={inputClass}
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    <span className="flex items-center gap-1"><Mail size={11} /> Email</span>
                  </label>
                  <input type="email" value={selectedRow.branchEmail} placeholder="Enter branch email"
                    onChange={(e) => { setSelectedRow({ ...selectedRow, branchEmail: e.target.value }); setIsEditing(true); }}
                    className={inputClass}
                  />
                </div>

                {/* Contact */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    <span className="flex items-center gap-1"><Phone size={11} /> Contact Number</span>
                  </label>
                  <input type="text" value={selectedRow.contactNumber} placeholder="Enter contact number"
                    onChange={(e) => { setSelectedRow({ ...selectedRow, contactNumber: e.target.value }); setIsEditing(true); }}
                    className={inputClass}
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    <span className="flex items-center gap-1"><MapPin size={11} /> Location</span>
                  </label>
                  <input type="text" value={selectedRow.location} placeholder="Enter location"
                    onChange={(e) => { setSelectedRow({ ...selectedRow, location: e.target.value }); setIsEditing(true); }}
                    className={inputClass}
                  />
                </div>

                {/* Address */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Address</label>
                  <input type="text" value={selectedRow.address} placeholder="Enter address"
                    onChange={(e) => { setSelectedRow({ ...selectedRow, address: e.target.value }); setIsEditing(true); }}
                    className={inputClass}
                  />
                </div>

                {/* Manager dropdown */}
                <div ref={dropdownRef}>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    <span className="flex items-center gap-1"><User size={11} /> Manager</span>
                  </label>
                  <div className="relative mt-1">
                    <input
                      type="text"
                      placeholder="Select manager"
                      value={selectedRow.managerName || ""}
                      onClick={() => setShowManagerDropdown((prev) => !prev)}
                      readOnly
                      className={inputClass + " cursor-pointer pr-9"}
                    />
                    <button type="button" className="absolute inset-y-0 right-0 px-3 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowManagerDropdown((prev) => !prev)}
                    >
                      <ChevronDown size={15} className={`transition-transform ${showManagerDropdown ? "rotate-180" : ""}`} />
                    </button>

                    {showManagerDropdown && (
                      <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                        {isManagersLoading ? (
                          <li className="px-3 py-2.5 text-sm text-gray-400">Loading managers...</li>
                        ) : isManagersError ? (
                          <li className="px-3 py-2.5 text-sm text-red-400">Failed to load managers</li>
                        ) : managers.length > 0 ? (
                          managers.map((manager) => (
                            <li key={manager.id}
                              onClick={() => { setSelectedRow({ ...selectedRow, managerName: manager.name, managerId: manager.id }); setIsEditing(true); setShowManagerDropdown(false); }}
                              className={`px-3 py-2.5 text-sm cursor-pointer hover:bg-[#e4ebf8] hover:text-[#143887] transition-colors ${selectedRow.managerId === manager.id ? "bg-[#e4ebf8] text-[#143887] font-medium" : "text-gray-700"}`}
                            >
                              {manager.name}
                            </li>
                          ))
                        ) : (
                          <li className="px-3 py-2.5 text-sm text-gray-400">No managers found</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Status — not editable here; use Activate/Deactivate actions instead */}
                {/* <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</label>
                  <select
                    value={selectedRow.status}
                    onChange={(e) => { setSelectedRow({ ...selectedRow, status: e.target.value }); setIsEditing(true); }}
                    className={inputClass}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div> */}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-3 mt-6 pt-5 border-t border-gray-100">
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-xl bg-[#143887] text-white text-sm font-semibold hover:bg-[#0f2d6b] transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isEditing ? "Save Changes" : "Update"}
                </button>
                <button
                  type="button"
                  onClick={() => { setSelectedRow(null); setIsEditing(false); }}
                  className="px-6 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                {isEditing && (
                  <span className="text-xs text-orange-500 font-medium ml-1">Unsaved changes</span>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      <FleetrifyModal
        isOpen={showDeleteModal}
        title="Confirm Deletion"
        onClose={() => setShowDeleteModal(false)}
        className="max-w-md h-fit"
      >
        <div className="flex flex-col items-center justify-center p-6 text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
            <GitBranch size={20} className="text-red-500" />
          </div>
          <div>
            <p className="text-gray-700 font-medium">Delete Branch</p>
            <p className="text-sm text-gray-500 mt-1">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-red-600">{branchToDelete?.branchName}</span>?
              This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-center gap-3 w-full">
            <button onClick={() => setShowDeleteModal(false)} disabled={isDeleting}
              className="flex-1 px-4 py-2.5 text-sm bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button onClick={handleConfirmDelete} disabled={isDeleting}
              className="flex-1 px-4 py-2.5 text-sm bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors font-medium"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </FleetrifyModal>
    </div>
  );
};

export default BranchTable;